import { dollars } from "./money";
import { computeMatches, inferOpenGaps, sourceRowsFor } from "./matchEngine";
import type { BuyerFundingReview, FundingPurpose, OfficeAction, PaymentRail } from "./types";

type FixturePatch = Partial<BuyerFundingReview> & {
  expected?: number;
  received?: number;
  initiated?: number;
  purpose?: FundingPurpose;
  rail?: PaymentRail;
  action?: OfficeAction;
};

const baseTime = "2026-06-01T16:00:00.000Z";

export function createReviewFixture(id: string, name: string, patch: FixturePatch = {}): BuyerFundingReview {
  const expected = patch.expected ?? 184762.19;
  const received = patch.received;
  const initiated = patch.initiated ?? expected;
  const purpose = patch.purpose ?? "Cash to close";
  const rail = patch.rail ?? "Wire";
  const review: BuyerFundingReview = {
    id,
    reviewNumber: `BFR-SYN-${id.toUpperCase()}`,
    officeId: "office-synthetic",
    officeName: "Synthetic Escrow Office",
    fileNumber: `SYN-${id.toUpperCase()}`,
    propertyAddress: "100 Synthetic File Way",
    closingDate: "2026-06-04",
    buyerParties: [
      {
        id: `${id}-buyer`,
        name: "Synthetic Buyer",
        role: "buyer",
        expectedFundingParty: true
      }
    ],
    fundingPurpose: purpose,
    expectedAmount: dollars(expected),
    dueAt: "2026-06-03T21:00:00.000Z",
    reviewReason: purpose,
    status: "Draft",
    fundsStatus: "Received - matched",
    expectedFunds: {
      amount: dollars(expected),
      purpose,
      dueAt: "2026-06-03T21:00:00.000Z",
      sourceDocumentId: "Final settlement statement v3",
      sourceStatus: "final",
      preparedBy: "Synthetic settlement team",
      extractionConfidence: 0.98
    },
    fundingInstruction: {
      versionLabel: "v3",
      sourceType: "Office wire instruction PDF",
      sentBy: "Synthetic Escrow Officer",
      sentTo: "Synthetic Buyer",
      sentAt: "2026-06-01T15:00:00.000Z",
      deliveryChannel: "Secure portal",
      receivingBankName: "Example Trust Bank",
      receivingAccountLast4: "9012",
      routingLast4: "4400",
      beneficiaryName: "Synthetic Escrow Trust Account",
      allowedRails: [rail],
      memoRequired: `SYN-${id.toUpperCase()}`,
      deadlineAt: "2026-06-03T21:00:00.000Z",
      officeTemplateStatus: "yes",
      hash: `sha256-${id}-instruction`
    },
    buyerAcknowledgment: {
      type: "Portal acknowledgment",
      partyName: "Synthetic Buyer",
      acknowledgedAmount: dollars(expected),
      acknowledgedInstructionVersion: "v3",
      acknowledgedRail: rail,
      acknowledgedAt: "2026-06-01T15:20:00.000Z",
      channel: "Secure portal",
      sourceDocumentId: "Buyer portal acknowledgment",
      recordedBy: "Synthetic Escrow Officer",
      limitation: "Buyer acknowledgment recorded. It does not decide office reliance."
    },
    fundingSource: {
      fundingPartyName: "Synthetic Buyer",
      relationshipToBuyer: "Self",
      partyType: "Buyer",
      sourceType: "buyer_personal",
      approvedUnderPolicy: true,
      limitation: "Funding source recorded under synthetic office policy."
    },
    sourceAccountEvidence: {
      evidenceType: "Plaid account owner evidence",
      provider: "plaid",
      recordedAt: "2026-06-01T15:30:00.000Z",
      bankName: "Example Bank",
      accountLast4: "9012",
      accountType: "Checking",
      ownerName: "Synthetic Buyer",
      ownerMatchResult: "Matched",
      balanceRecorded: true,
      balanceAmount: dollars(expected + 5000),
      balanceAsOf: "2026-06-01T15:30:00.000Z",
      sourceParty: "Synthetic Buyer",
      limitation:
        "Source account evidence recorded through provider output. This is evidence for the review, not a promise of settlement or final funds availability."
    },
    paymentRail: {
      rail,
      allowedByInstructions: "yes",
      allowedByPolicy: "yes",
      expectedArrivalTiming: "Same business day",
      returnRiskNote: rail.includes("ACH") ? "ACH return risk remains open until final." : "No return risk noted in source row."
    },
    paymentInitiation: {
      evidenceType: `${rail} receipt`,
      rail,
      initiatedAmount: dollars(initiated),
      initiatedAt: "2026-06-02T15:44:00.000Z",
      initiatedBy: "Synthetic Buyer",
      originatingBank: "Example Bank",
      senderShown: "Synthetic Buyer",
      destinationShown: "Example Trust Bank account ending 9012",
      traceNumber: "wire trace ending 1000",
      reference: `SYN-${id.toUpperCase()}`,
      fileMemo: `SYN-${id.toUpperCase()}`,
      sourceDocumentId: "Buyer payment receipt",
      limitation: "Initiation evidence does not prove final receipt or source match."
    },
    inboundReceipt: {
      sourceType: "trust_ledger",
      amountReceived: received === undefined ? dollars(expected) : dollars(received),
      receivedAt: "2026-06-02T22:18:00.000Z",
      receivingAccountLast4: "9012",
      senderShown: "Synthetic Buyer",
      originatingBank: "Example Bank",
      rail,
      traceNumber: "wire trace ending 1000",
      reference: `SYN-${id.toUpperCase()}`,
      fileNumber: `SYN-${id.toUpperCase()}`,
      trustLedgerEntryId: `TL-${id.toUpperCase()}`,
      depositStatus: "Received",
      returnRiskStatus: "none_recorded",
      appliedToFile: false,
      accountingReviewer: "Synthetic Accounting Reviewer"
    },
    matches: [],
    openGaps: [],
    officeAction: {
      action: patch.action ?? "Accepted for file",
      fundsStatus: "Received - matched",
      note: "Synthetic fixture office action recorded.",
      recordedBy: "Synthetic Escrow Officer",
      recordedAt: baseTime
    },
    signoff: {
      signerName: "Synthetic Escrow Officer",
      role: "Escrow officer",
      office: "Synthetic Escrow Office",
      authenticationMethod: "Cloudflare Access",
      signedAt: baseTime,
      fundsStatusAtSignoff: "Received - matched",
      gapsAcknowledged: true,
      officeAction: patch.action ?? "Accepted for file",
      statementVersion: "bfr-signoff-v1"
    },
    sourceRows: [],
    events: [],
    saveBackLocation: "Escrow file funding review tab",
    receiptVersion: "v1",
    createdBy: "Synthetic Escrow Officer",
    createdAt: "2026-06-01T14:00:00.000Z",
    updatedAt: baseTime,
    ...patch
  };
  review.sourceRows = sourceRowsFor(review);
  review.matches = computeMatches(review);
  review.openGaps = inferOpenGaps(review);
  review.events = eventLogFor(review, name);
  return review;
}

export const FIXTURES: BuyerFundingReview[] = [
  createReviewFixture("clean-wire", "Clean cash-to-close wire"),
  createReviewFixture("emd-ach-not-final", "EMD ACH not final", {
    expected: 25000,
    received: 0,
    purpose: "Earnest money deposit",
    rail: "ACH",
    fundsStatus: "Held",
    action: "Held pending review",
    inboundReceipt: {
      sourceType: "payment_provider",
      amountReceived: undefined,
      receivedAt: undefined,
      rail: "ACH",
      senderShown: "Synthetic Buyer",
      receivingAccountLast4: "9012",
      returnRiskStatus: "open",
      depositStatus: "Pending",
      accountingReviewer: "Synthetic Accounting Reviewer"
    }
  }),
  createReviewFixture("source-mismatch", "Source account mismatch", {
    action: "Requested source evidence",
    fundsStatus: "Held",
    fundingSource: {
      fundingPartyName: "Smith Family Trust",
      relationshipToBuyer: "Trust",
      partyType: "Trust",
      sourceType: "trust",
      approvedUnderPolicy: false,
      authorityEvidenceAttached: false,
      limitation: "Trust authority evidence not recorded."
    },
    sourceAccountEvidence: {
      evidenceType: "Plaid account owner evidence",
      provider: "plaid",
      recordedAt: "2026-06-01T15:30:00.000Z",
      ownerName: "John Smith",
      ownerMatchResult: "Did not match",
      sourceParty: "John Smith",
      limitation: "Owner evidence does not match buyer or office-recorded funding source."
    },
    inboundReceipt: {
      sourceType: "trust_ledger",
      amountReceived: dollars(184762.19),
      receivedAt: "2026-06-02T22:18:00.000Z",
      receivingAccountLast4: "9012",
      senderShown: "Smith Family Trust",
      rail: "Wire",
      reference: "SYN-SOURCE-MISMATCH",
      fileNumber: "SYN-SOURCE-MISMATCH",
      trustLedgerEntryId: "TL-SOURCE-MISMATCH",
      returnRiskStatus: "none_recorded"
    }
  }),
  createReviewFixture("gift-funds", "Third-party gift funds", {
    expected: 80000,
    purpose: "Gift funds",
    fundingSource: {
      fundingPartyName: "Synthetic Gift Donor",
      relationshipToBuyer: "Parent",
      partyType: "Gift donor",
      sourceType: "gift",
      approvedUnderPolicy: true,
      giftEvidenceAttached: true,
      limitation: "Gift letter recorded with split source."
    },
    inboundReceipt: {
      sourceType: "trust_ledger",
      amountReceived: dollars(80000),
      receivedAt: "2026-06-02T22:18:00.000Z",
      receivingAccountLast4: "9012",
      senderShown: "Synthetic Gift Donor",
      rail: "Wire",
      reference: "SYN-GIFT-FUNDS",
      fileNumber: "SYN-GIFT-FUNDS",
      trustLedgerEntryId: "TL-GIFT-FUNDS",
      returnRiskStatus: "none_recorded"
    }
  }),
  createReviewFixture("stale-instruction", "Buyer used stale instruction", {
    action: "Requested revised instructions",
    fundsStatus: "Not requested",
    buyerAcknowledgment: {
      type: "Email reply",
      partyName: "Synthetic Buyer",
      acknowledgedAmount: dollars(184762.19),
      acknowledgedInstructionVersion: "v2",
      acknowledgedRail: "Wire",
      acknowledgedAt: "2026-06-01T15:20:00.000Z",
      channel: "Email",
      recordedBy: "Synthetic Escrow Officer",
      limitation: "Acknowledgment references prior instruction version."
    },
    inboundReceipt: {
      sourceType: "not_received",
      returnRiskStatus: "unknown"
    }
  }),
  createReviewFixture("overfunding", "Overfunding", {
    expected: 140000,
    received: 145000,
    action: "Recorded overage",
    fundsStatus: "Held"
  }),
  createReviewFixture("shortage", "Shortage", {
    expected: 140000,
    received: 137500,
    action: "Requested additional funds",
    fundsStatus: "Received - partially matched"
  }),
  createReviewFixture("cashiers-check", "Cashier's check", {
    expected: 10000,
    purpose: "Earnest money deposit",
    rail: "Cashier's check",
    sourceAccountEvidence: {
      evidenceType: "Cashier's check image",
      provider: "manual",
      ownerMatchResult: "Not applicable",
      limitation: "Cashier's check evidence recorded with deposit receipt under office policy."
    }
  }),
  createReviewFixture("manual-only", "Manual-only source evidence", {
    sourceAccountEvidence: {
      evidenceType: "Bank statement",
      provider: "bank_statement",
      recordedAt: "2026-06-01T15:30:00.000Z",
      ownerName: "Synthetic Buyer",
      ownerMatchResult: "Matched",
      sourceParty: "Synthetic Buyer",
      limitation: "Manual evidence retained. No live account evidence was recorded."
    },
    action: "Recorded exception"
  }),
  createReviewFixture("provider-conflict", "Payment provider status conflict", {
    action: "Held pending review",
    fundsStatus: "Held",
    inboundReceipt: {
      sourceType: "payment_provider",
      amountReceived: undefined,
      rail: "Wire",
      senderShown: "Synthetic Buyer",
      receivingAccountLast4: "9012",
      depositStatus: "Provider says delivered; trust ledger not updated",
      returnRiskStatus: "unknown",
      accountingReviewer: "Synthetic Accounting Reviewer"
    }
  }),
  createReviewFixture("wrong-memo", "Wrong memo / no file number", {
    action: "Held pending review",
    fundsStatus: "Received - not matched",
    inboundReceipt: {
      sourceType: "trust_ledger",
      amountReceived: dollars(184762.19),
      receivedAt: "2026-06-02T22:18:00.000Z",
      receivingAccountLast4: "9012",
      senderShown: "Synthetic Buyer",
      rail: "Wire",
      reference: "NO FILE NUMBER",
      trustLedgerEntryId: "TL-WRONG-MEMO",
      returnRiskStatus: "none_recorded"
    }
  }),
  createReviewFixture("ach-return", "ACH return", {
    expected: 25000,
    purpose: "Earnest money deposit",
    rail: "ACH",
    action: "Returned / rejected funds",
    fundsStatus: "Reversed / returned by bank",
    inboundReceipt: {
      sourceType: "ach_report",
      amountReceived: dollars(25000),
      receivedAt: "2026-06-02T22:18:00.000Z",
      receivingAccountLast4: "9012",
      senderShown: "Synthetic Buyer",
      rail: "ACH",
      reference: "SYN-ACH-RETURN",
      fileNumber: "SYN-ACH-RETURN",
      trustLedgerEntryId: "TL-ACH-RETURN",
      returnRiskStatus: "returned"
    }
  })
];

function eventLogFor(review: BuyerFundingReview, label: string) {
  const actorId = review.createdBy;
  return [
    "Review created",
    "Expected funds recorded",
    "Funding instruction recorded",
    "Buyer acknowledgment recorded",
    "Source account evidence recorded",
    "Payment initiation evidence recorded",
    "Inbound receipt recorded",
    "Four-way match generated",
    "Open gaps recorded",
    "Office action recorded",
    "Reviewer signed",
    "Receipt exported"
  ].map((eventType, index) => ({
    id: `${review.id}-event-${index + 1}`,
    reviewId: review.id,
    actorId,
    eventType,
    occurredAt: new Date(new Date(baseTime).getTime() + index * 60000).toISOString(),
    objectType: label,
    sourceRowIds: review.sourceRows.slice(0, Math.max(1, Math.min(review.sourceRows.length, index))).map((row) => row.id)
  }));
}
